// Role-aware navigation: works out WHERE to send a user from the roles they hold,
// instead of hardcoding a landing page (/account/dashboard, subbtn[0], subTab[0]).
//
// A screen counts as openable only when BOTH hold:
//   1. the user has the menu node's rolename (and every parent's) in ListOfPaths.jsx, and
//   2. the user has the rolename of the route registered for that url in AllRoutes —
//      App.js only mounts routes the user holds, so landing on any other url ends on
//      Access Denied. A few menu entries carry a different role than their route (e.g.
//      PDC), so checking the menu alone could redirect straight into Access Denied.
import { AdminRoutes } from "../component/AllRoutes/index.js";
import {
  accountsPathsArr,
  assetsArray,
  clientWorkArray,
  commissionArray,
  contractArray,
  crmArray,
  hrArray,
  inventoryArray,
  operationsArray,
  procurementArray,
  propertyManagementArray,
  registryArray,
  salesArray,
  settingsPathArr,
  userPortalArray,
  worksArray,
} from "../ListOfPaths/ListOfPaths.jsx";

const matchesModule = (path, target) => {
  const clean = (path || "").toLowerCase();
  const pattern = target
    .toLowerCase()
    .replace(/^\/+|\/+$/g, "")
    .replace(/\//g, "\\/");
  return new RegExp(`(^|\\/)${pattern}(\\/|\\?|$)`).test(clean);
};

export const isCommissionAuthorizedUser = (email) =>
  `${email || ""}`.trim().toLowerCase() === "admin360@aiksol.com";

// Sidebar menu for a url. Uses path segment boundaries so words containing
// "hr" (e.g. "three-way-match") do not mistakenly route to HR.
export const menuForPath = (path = "", userEmail = "") =>
  matchesModule(path, "commission")
    ? (isCommissionAuthorizedUser(userEmail) ? commissionArray : [])
    : matchesModule(path, "account")
      ? accountsPathsArr
      : matchesModule(path, "inventory")
        ? inventoryArray
        : matchesModule(path, "hr")
          ? hrArray
          : matchesModule(path, "crm")
            ? crmArray
            : matchesModule(path, "asset")
              ? assetsArray
              : /^\/settings(?:\/|\?|$)/i.test(path)
                ? settingsPathArr
                : matchesModule(path, "user")
                  ? userPortalArray
                  : matchesModule(path, "client-work")
                    ? clientWorkArray
                    : matchesModule(path, "works") || matchesModule(path, "store/contractors")
                      ? worksArray
                      : matchesModule(path, "procurement") || matchesModule(path, "store/vendors")
                        ? procurementArray
                        : matchesModule(path, "store")
                          ? operationsArray
                          : matchesModule(path, "sales")
                            ? salesArray
                            : matchesModule(path, "contract")
                              ? contractArray
                              : matchesModule(path, "property") ||
                                  matchesModule(path, "property-management")
                                ? propertyManagementArray
                                : matchesModule(path, "registry")
                                  ? registryArray
                                  : [];

const routeByPath = new Map(
  AdminRoutes.filter((route) => route.path).map((route) => [route.path, route]),
);

// True when App.js would actually mount a screen for this url for this user.
export const canOpenUrl = (url, userRoles = [], userEmail = "") => {
  if (url && url.startsWith("/commission")) {
    return isCommissionAuthorizedUser(userEmail);
  }
  const route = routeByPath.get(url);
  return !!(route?.rolename && userRoles.includes(route.rolename));
};

// Same visibility rules Layout.js applies to the sidebar: the node's role, plus
// `requiresProject` items being hidden in Main Chart of Account mode.
const isNodeVisible = (node, userRoles, hasProject, userEmail) => {
  if (node?.url && node.url.startsWith("/commission")) {
    return isCommissionAuthorizedUser(userEmail);
  }
  return (
    !!node &&
    (!node.rolename || userRoles.includes(node.rolename)) &&
    !(node.requiresProject && !hasProject)
  );
};

// Layout.js hides these whole sections when no specific project is selected.
const PROJECT_ONLY_SECTIONS = ["Vouchers", "Investment"];

// First tab of a voucher-style tab strip (subTab) the user can open, or null.
export const getFirstAllowedTabUrl = (tabs, userRoles = [], { hasProject = true } = {}, userEmail = "") => {
  const tab = (tabs || []).find(
    (t) => isNodeVisible(t, userRoles, hasProject, userEmail) && canOpenUrl(t.url, userRoles, userEmail),
  );
  return tab ? tab.url : null;
};

// Walks menu -> subbtn -> subTab in sidebar order and returns the url of the first
// screen the user may open, or null when there is none (caller shows Access Denied).
export const getFirstAllowedUrl = (menu, userRoles = [], { hasProject = true } = {}, userEmail = "") => {
  for (const item of menu || []) {
    if (!isNodeVisible(item, userRoles, hasProject, userEmail)) {
      continue;
    }
    if (!hasProject && PROJECT_ONLY_SECTIONS.includes(item.text)) {
      continue;
    }

    for (const sub of item.subbtn || []) {
      if (!isNodeVisible(sub, userRoles, hasProject, userEmail)) {
        continue;
      }
      if (sub.subTab) {
        const tabUrl = getFirstAllowedTabUrl(sub.subTab, userRoles, { hasProject }, userEmail);
        if (tabUrl) {
          return tabUrl;
        }
      } else if (canOpenUrl(sub.url, userRoles, userEmail)) {
        return sub.url;
      }
    }

    // Leaf items (Dashboard, Units, ...) and groups that also carry their own url.
    if (item.url && canOpenUrl(item.url, userRoles, userEmail)) {
      return item.url;
    }
  }
  return null;
};

// Module dashboards and settings entry points (login, project selection, top
// nav, settings dropdown). When one is forbidden or unspecific, we redirect to
// the first allowed screen of that module instead of showing Access Denied.
export const isModuleLanding = (path = "") =>
  /\/dashboard\/?$/.test(path) ||
  /^\/settings(\/(users)?)?\/?$/.test(path) ||
  /^\/procurement(\/(purchase-requisition|quotations(\/quotes|\/comparison)?|vendors(\/purchase-order)?))?\/?$/.test(
    path,
  ) ||
  /^\/client-work(\/(dashboard)?)?\/?$/.test(path) ||
  /^\/works(\/(bill-of-quantities|boq(\/bill-of-quantities)?|rate-library|work-orders(\/wo)?|contractor-bills|measurement-book)?)?\/?$/.test(
    path,
  );
